import { useEffect, useMemo, useRef } from "react";
import { useNavigate } from "@tanstack/react-router";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import type { LocationPlace } from "../api";
import { countryName } from "../../../lib/country";
import { COUNTRY_POINTS } from "./countries";

const IMAGERY = "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}";
const LABELS = "https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}";
const ATTRIBUTION = "Tiles © Esri — Source: Esri, Maxar, Earthstar Geographics, and the GIS User Community";
const GROUP_PX = 34;

interface Spot {
  key: string;
  code: string;
  name: string;
  lat: number;
  lon: number;
  sessions: number;
}

function countLabel(n: number): string {
  if (n < 1000) return String(n);
  const digits = n < 10000 ? 1 : 0;
  return `${(n / 1000).toFixed(digits)}k`.replace(".0k", "k");
}

function sessionsText(n: number): string {
  return `${n.toLocaleString()} ${n === 1 ? "session" : "sessions"}`;
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]!);
}

/** Cities with a known center, plus a country mark for sessions placed only by country. */
function spotsFrom(counts: Array<{ code: string; sessions: number }>, places: LocationPlace[]): Spot[] {
  const spots: Spot[] = [];
  const placed = new Map<string, number>();
  for (const p of places) {
    if (typeof p.lat !== "number" || typeof p.lon !== "number" || !p.city) continue;
    spots.push({
      key: `${p.code}:${p.region ?? ""}:${p.city}`,
      code: p.code,
      name: [p.city, p.region, countryName(p.code)].filter(Boolean).join(", "),
      lat: p.lat,
      lon: p.lon,
      sessions: p.sessions,
    });
    placed.set(p.code, (placed.get(p.code) ?? 0) + p.sessions);
  }
  for (const c of counts) {
    const rest = c.sessions - (placed.get(c.code) ?? 0);
    const point = COUNTRY_POINTS[c.code];
    if (rest <= 0 || !point) continue;
    spots.push({ key: c.code, code: c.code, name: `${countryName(c.code)} (city unknown)`, lat: point.lat, lon: point.lon, sessions: rest });
  }
  return spots;
}

interface Group {
  spots: Spot[];
  sessions: number;
  lat: number;
  lon: number;
}

/** Spots that would overlap on screen at this zoom are shown as one numbered mark. */
function groupSpots(map: L.Map, spots: Spot[]): Group[] {
  const groups: Array<Group & { x: number; y: number }> = [];
  const ordered = [...spots].sort((a, b) => b.sessions - a.sessions);
  for (const spot of ordered) {
    const pt = map.project([spot.lat, spot.lon], map.getZoom());
    const near = groups.find((g) => Math.hypot(g.x - pt.x, g.y - pt.y) < GROUP_PX);
    if (near) {
      near.spots.push(spot);
      near.sessions += spot.sessions;
      continue;
    }
    groups.push({ spots: [spot], sessions: spot.sessions, lat: spot.lat, lon: spot.lon, x: pt.x, y: pt.y });
  }
  return groups;
}

function markSize(sessions: number, max: number): number {
  return Math.round(26 + 18 * Math.sqrt(sessions / Math.max(1, max)));
}

export function WorldMap({ counts, places }: { counts: Array<{ code: string; sessions: number; share: number }>; places: LocationPlace[] }) {
  const navigate = useNavigate();
  const el = useRef<HTMLDivElement>(null);
  const mapRef = useRef<L.Map | null>(null);
  const layerRef = useRef<L.LayerGroup | null>(null);
  const fitted = useRef(false);
  const spots = useMemo(() => spotsFrom(counts, places), [counts, places]);
  const openRef = useRef((code: string) => void navigate({ to: "/", search: { country: code } as never }));
  openRef.current = (code: string) => void navigate({ to: "/", search: { country: code } as never });

  useEffect(() => {
    if (!el.current || mapRef.current) return;
    const map = L.map(el.current, {
      center: [25, -20],
      zoom: 2,
      minZoom: 2,
      maxZoom: 12,
      worldCopyJump: true,
      maxBounds: [[-85, -Infinity], [85, Infinity]],
      zoomSnap: 0.5,
    });
    L.tileLayer(IMAGERY, { attribution: ATTRIBUTION, maxZoom: 19 }).addTo(map);
    L.tileLayer(LABELS, { maxZoom: 19, pane: "overlayPane" }).addTo(map);
    layerRef.current = L.layerGroup().addTo(map);
    mapRef.current = map;
    const ro = new ResizeObserver(() => map.invalidateSize());
    ro.observe(el.current);
    return () => {
      ro.disconnect();
      map.remove();
      mapRef.current = null;
      layerRef.current = null;
      fitted.current = false;
    };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    const layer = layerRef.current;
    if (!map || !layer) return;

    if (!fitted.current && spots.length > 0) {
      fitted.current = true;
      const bounds = L.latLngBounds(spots.map((s) => [s.lat, s.lon] as [number, number]));
      map.fitBounds(bounds.pad(0.25), { maxZoom: 5, animate: false });
    }

    const draw = () => {
      layer.clearLayers();
      const groups = groupSpots(map, spots);
      const max = Math.max(1, ...groups.map((g) => g.sessions));
      for (const g of groups) {
        const size = markSize(g.sessions, max);
        const many = g.spots.length > 1;
        const icon = L.divIcon({
          className: "",
          html: `<span class="traffic-pin${many ? " group" : ""}" style="width:${size}px;height:${size}px">${countLabel(g.sessions)}</span>`,
          iconSize: [size, size],
          iconAnchor: [size / 2, size / 2],
        });
        const first = g.spots[0]!;
        const title = many ? `${g.spots.length} places · ${sessionsText(g.sessions)}` : `${first.name} · ${sessionsText(g.sessions)}`;
        const marker = L.marker([g.lat, g.lon], { icon, keyboard: true, title, riseOnHover: true, alt: title });
        const lines = g.spots
          .slice(0, 8)
          .map((s) => `<div class="traffic-tip-row"><span>${escapeHtml(s.name)}</span><b>${s.sessions.toLocaleString()}</b></div>`)
          .join("");
        const more = g.spots.length > 8 ? `<div class="traffic-tip-more">and ${g.spots.length - 8} more</div>` : "";
        marker.bindTooltip(`${lines}${more}`, { direction: "top", offset: [0, -size / 2], className: "traffic-tooltip" });
        marker.on("click", () => {
          if (many && map.getZoom() < map.getMaxZoom()) {
            const b = L.latLngBounds(g.spots.map((s) => [s.lat, s.lon] as [number, number]));
            map.flyToBounds(b.pad(0.4), { maxZoom: Math.min(map.getMaxZoom(), map.getZoom() + 3), duration: 0.5 });
            return;
          }
          openRef.current(first.code);
        });
        marker.addTo(layer);
      }
    };
    draw();
    map.on("zoomend", draw);
    return () => {
      map.off("zoomend", draw);
    };
  }, [spots]);

  return (
    <div className="traffic-map">
      <div ref={el} className="traffic-leaflet" role="region" aria-label="Map of sessions by place" />
      <p className="traffic-tip">
        {spots.length
          ? "Each number is the sessions from that place. Click a grouped number to zoom in, or a single place to open its sessions."
          : "No sessions in this range have a location yet."}
      </p>
    </div>
  );
}
